import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {Dispatch} from "react-redux";
import {api} from "../../../api/api";
import Appointment from "../../../model/appointment/Appointment";
import {
	APPOINTMENT_CREATED,
	APPOINTMENT_DELETED,
	APPOINTMENT_TYPES_FETCHED,
	APPOINTMENT_UPDATED,
	JOB_APPOINTMENT_CREATED,
	JOB_APPOINTMENT_DELETED,
	JOB_APPOINTMENT_TYPES_FETCHED,
	JOB_APPOINTMENT_UPDATED,
	JOB_APPOINTMENTS_FETCHED,
	JOB_APPOINTMENTS_IN_DATE_RANGE_FETCHED,
	LEAD_APPOINTMENTS_FETCHED,
	LEAD_APPOINTMENTS_IN_DATE_RANGE_FETCHED
} from "./actionType";
import {Action} from "redux-actions";
import {fetchLeadEvents} from "../lead/lead";
import AppointmentType from "../../../model/appointment/AppointmentType";
import {SalesEngineerApiDto} from "../../../api/model/rest";
import JobAppointment from "../../../model/appointment/job/JobAppointment";
import {fetchJobEvents} from "../job/job";
import JobAppointmentType from "../../../model/appointment/job/JobAppointmentType";
import AppointmentDetails from "../../../model/appointment/AppointmentDetails";
import IndividualContact from "../../../model/contact/IndividualContact";
import {appointmentsIndividualContactsFetched} from "../contacts/contacts";
import {ContactsMap} from "../../reducer/contact/ContactsState";
import {SitesMap} from "../../reducer/site/SiteState";
import {appointmentsSitesFetched} from "../site/site";
import moment = require("moment");

/**
 * Appointments for single lead fetched successful action.
 * @param leadId
 * @param {Appointment[]} appointments
 * @returns {Action<LEAD_APPOINTMENTS_FETCHED>}
 */
function leadAppointmentsFetched(leadId: string, appointments: Appointment[]): Action<LEAD_APPOINTMENTS_FETCHED> {
	return {type: LEAD_APPOINTMENTS_FETCHED, payload: {appointments, leadId}};
}

/**
 * Appointments for single job fetched successful action.
 * @param {string} id of job
 * @param jobAppointments
 * @returns {Action<JOB_APPOINTMENTS_FETCHED>}
 */
function jobAppointmentsFetched(id: string, jobAppointments: JobAppointment[]): Action<JOB_APPOINTMENTS_FETCHED> {
	return {type: JOB_APPOINTMENTS_FETCHED, payload: {jobAppointments}};
}

/**
 * Appointment created successful action.
 * @param {Appointment} appointment
 * @returns {Action<APPOINTMENT_CREATED>}
 */
function appointmentCreated(appointment: Appointment): Action<APPOINTMENT_CREATED> {
	return {type: APPOINTMENT_CREATED, payload: {appointment}};
}

/**
 * Job Appointment created successful action.
 * @param {JobAppointment} jobAppointment
 * @returns {Action<JOB_APPOINTMENT_CREATED>}
 */
function jobAppointmentCreated(jobAppointment: JobAppointment): Action<JOB_APPOINTMENT_CREATED> {
	return {type: JOB_APPOINTMENT_CREATED, payload: {jobAppointment}};
}

/**
 * Appointment updated successful action.
 * @param {Appointment} appointment
 * @returns {Action<APPOINTMENT_UPDATED>}
 */
function appointmentUpdated(appointment: Appointment): Action<APPOINTMENT_UPDATED> {
	return {type: APPOINTMENT_UPDATED, payload: {appointment}};
}

/**
 * Job appointment updated successful action.
 * @param {JobAppointment} jobAppointment
 * @returns {Action<JOB_APPOINTMENT_UPDATED>}
 */
function jobAppointmentUpdated(jobAppointment: JobAppointment): Action<JOB_APPOINTMENT_UPDATED> {
	return {type: JOB_APPOINTMENT_UPDATED, payload: {jobAppointment}};
}

/**
 * Appointment deleted successful action.
 * @param {string} appointmentId
 * @param leadId
 * @returns {Action<APPOINTMENT_DELETED>}
 */
function appointmentDeleted(appointmentId: string, leadId: string): Action<APPOINTMENT_DELETED> {
	return {type: APPOINTMENT_DELETED, payload: {appointmentId, leadId}};
}

/**
 * Job appointment deleted successful action.
 * @param {string} appointmentId
 * @returns {Action<JOB_APPOINTMENT_DELETED>}
 */
function jobAppointmentDeleted(appointmentId: string): Action<JOB_APPOINTMENT_DELETED> {
	return {type: JOB_APPOINTMENT_DELETED, payload: {appointmentId}};
}

/**
 * Appointment types fetched successful action.
 * @param {AppointmentType[]} appointmentTypes
 * @returns {Action<APPOINTMENT_TYPES_FETCHED>}
 */
function appointmentTypesFetched(appointmentTypes: AppointmentType[]): Action<APPOINTMENT_TYPES_FETCHED> {
	return {type: APPOINTMENT_TYPES_FETCHED, payload: {appointmentTypes}};
}

/**
 * Job appointment types fetched successful action.
 * @returns {Action<JOB_APPOINTMENT_TYPES_FETCHED>}
 * @param jobAppointmentTypes
 */
function jobAppointmentTypesFetched(jobAppointmentTypes: JobAppointmentType[]): Action<JOB_APPOINTMENT_TYPES_FETCHED> {
	return {type: JOB_APPOINTMENT_TYPES_FETCHED, payload: {jobAppointmentTypes}};
}

/**
 * Lead appointments fetched successful action.
 * @returns {Action<LEAD_APPOINTMENTS_IN_DATE_RANGE_FETCHED>}
 * @param appointments
 */
function leadAppointmentsInDateRangeFetched(appointments: { [key: string]: Appointment[] }): Action<LEAD_APPOINTMENTS_IN_DATE_RANGE_FETCHED> {
	return {type: LEAD_APPOINTMENTS_IN_DATE_RANGE_FETCHED, payload: {appointments}};
}

/**
 * Job appointments fetched successful action.
 * @returns {Action<JOB_APPOINTMENTS_IN_DATE_RANGE_FETCHED>}
 * @param appointments
 */
function jobAppointmentsInDateRangeFetched(appointments: JobAppointment[]): Action<JOB_APPOINTMENTS_IN_DATE_RANGE_FETCHED> {
	return {type: JOB_APPOINTMENTS_IN_DATE_RANGE_FETCHED, payload: {appointments}};
}

/**
 * Fetch lead appointments action
 * @param {string} leadId lead identifier.
 * @param {OnSuccessData<Appointment[]>} onSuccess on successful fetch action callback.
 * @param {OnFailure} onFailure on failure fetch action callback.
 */
export function fetchLeadAppointments(leadId: string, onSuccess: OnSuccessData<Appointment[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.fetchLeadAppointments(leadId).then(appointments => {
			dispatch(leadAppointmentsFetched(leadId, appointments));
			onSuccess(appointments);
		}).catch(onFailure);
	}
}

/**
 * Create lead appointment action.
 * @param {string} leadId
 * @param {Appointment} appointment
 * @param {OnSuccessData<Appointment>} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function createAppointment(leadId: string,
	appointment: Appointment,
	onSuccess: OnSuccessData<Appointment>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.createAppointment(leadId, appointment.toJson()).then(app => {
			dispatch(appointmentCreated(app));
			dispatch(fetchLeadEvents(leadId, () => {}, () => {}));
			onSuccess(app);
		}).catch(onFailure);
	}
}


/**
 * Update lead appointment action.
 * @param {string} leadId
 * @param {Appointment} appointment
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function updateAppointment(leadId: string, appointment: Appointment, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.updateAppointment(leadId, appointment.id, appointment.toJson()).then(() => {
			dispatch(appointmentUpdated(appointment));
			dispatch(fetchLeadEvents(leadId, () => {}, () => {}));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Delete lead appointment action.
 * @param {string} appointmentId
 * @param leadId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function deleteAppointment(appointmentId: string, leadId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.deleteAppointment(appointmentId).then(() => {
			dispatch(appointmentDeleted(appointmentId, leadId));
			onSuccess()
		}).catch(onFailure)
	}
}

/**
 * Fetch appointments action
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function fetchAppointmentTypes(onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.fetchAppointmentTypes().then(appointmentTypes => {
			onSuccess();
			dispatch(appointmentTypesFetched(appointmentTypes))
		}).catch(onFailure)
	}
}

/**
 * Send SMS to prospect with link to the profile url of sales engineer for lead appointment.
 * @param appointment
 * @param seData
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function sendLeadAppointmentSalesEngineerLinkSMS(appointment: Appointment,
	seData: SalesEngineerApiDto,
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.sendLeadAppointmentSalesEngineerLinkSMS(appointment.id, seData).then(() => {
			appointment.leadId.map(leadId => {
				dispatch(fetchLeadEvents(leadId, () => {}, () => {}));
			});
			onSuccess();
		}).catch(onFailure)
	}
}

/**
 * Fetch job appointments action
 * @param {string} jobId job identifier.
 * @param {OnSuccessData<JobAppointment[]>} onSuccess on successful fetch action callback.
 * @param {OnFailure} onFailure on failure fetch action callback.
 */
export function fetchJobAppointments(jobId: string, onSuccess: OnSuccessData<JobAppointment[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.fetchJobAppointments(jobId).then(appointments => {
			dispatch(jobAppointmentsFetched(jobId, appointments));
			onSuccess(appointments);
		}).catch(onFailure);
	}
}

/**
 * Create job appointment action.
 * @param {string} jobId
 * @param {JobAppointment} appointment
 * @param {OnSuccessData<JobAppointment>} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function createJobAppointment(jobId: string,
	appointment: JobAppointment,
	onSuccess: OnSuccessData<JobAppointment>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.createJobAppointment(jobId, appointment.toJson()).then(app => {
			dispatch(jobAppointmentCreated(app));
			dispatch(fetchJobEvents(jobId, () => {}, () => {}));
			onSuccess(app);
		}).catch(onFailure);
	}
}

/**
 * Update job appointment action.
 * @param {string} jobId
 * @param {JobAppointment} appointment
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function updateJobAppointment(jobId: string, appointment: JobAppointment, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.updateJobAppointment(jobId, appointment.id, appointment.toJson()).then(() => {
			dispatch(jobAppointmentUpdated(appointment));
			dispatch(fetchJobEvents(jobId, () => {}, () => {}));
			onSuccess();
		}).catch(onFailure);
	}
}

/**
 * Delete job appointment action.
 * @param {string} appointmentId
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function deleteJobAppointment(appointmentId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.deleteJobAppointment(appointmentId).then(() => {
			dispatch(jobAppointmentDeleted(appointmentId));
			onSuccess()
		}).catch(onFailure)
	}
}

/**
 * Fetch job appointments types action
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @returns {(dispatch: <any>) => void}
 */
export function fetchJobAppointmentTypes(onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.fetchJobAppointmentTypes().then(appointmentTypes => {
			onSuccess();
			dispatch(jobAppointmentTypesFetched(appointmentTypes))
		}).catch(onFailure)
	}
}

export function fetchLeadsAppointmentsInDateRange(dateFrom: moment.Moment,
	dateTo: moment.Moment,
	onSuccess: OnSuccessData<AppointmentDetails[]>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.fetchLeadsAppointments(dateFrom.clone().startOf("day").toISOString(),
			dateTo.clone().endOf("day").toISOString()).then(
			(app: AppointmentDetails[]) => {
				const contactsMap: ContactsMap = {};
				const appointmentsMaps: { [key: string]: Appointment[] } = {};
				const sitesMap: SitesMap = {};
				app.forEach(ad => {
					contactsMap[ad.individualContact.id] = ad.individualContact;
					const appointment = ad.appointment as Appointment
					appointment.leadId.map(lId => appointmentsMaps[lId] = (appointmentsMaps[lId] || []).concat(appointment))
					ad.site.map(s => sitesMap[s.id] = s);
				});
				dispatch(leadAppointmentsInDateRangeFetched(appointmentsMaps));
				dispatch(appointmentsIndividualContactsFetched(contactsMap))
				dispatch(appointmentsSitesFetched(sitesMap));
				onSuccess(app);
			}).catch(onFailure)
	}
}

export function fetchJobsAppointmentsInDateRange(dateFrom: moment.Moment,
	dateTo: moment.Moment,
	onSuccess: OnSuccessData<AppointmentDetails[]>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.appointmentApi.fetchJobsAppointments(dateFrom.clone().startOf("day").toISOString(),
			dateTo.clone().endOf("day").toISOString()).then(
			(app: AppointmentDetails[]) => {
				const contactsMap: { [key: string]: IndividualContact } = {};
				const appointments: JobAppointment[] = [];
				const sitesMap: SitesMap = {};
				app.forEach(ad => {
					contactsMap[ad.individualContact.id] = ad.individualContact;
					appointments.push(ad.appointment as JobAppointment);
					ad.site.map(s => sitesMap[s.id] = s);
				});
				dispatch(jobAppointmentsInDateRangeFetched(appointments));
				dispatch(appointmentsIndividualContactsFetched(contactsMap));
				dispatch(appointmentsSitesFetched(sitesMap));
				onSuccess(app);
			}).catch(onFailure)
	}
}